import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Clock, CheckCircle2, XCircle, Palmtree, Calendar, 
  User, MessageSquare, AlertCircle, Loader2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { companyApi } from '../../../lib/api';
import { format, parseISO, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const LEAVE_TYPES = [
  { value: 'vacation', label: 'Congés payés', color: 'bg-green-100 text-green-700' },
  { value: 'sick', label: 'Maladie', color: 'bg-red-100 text-red-700' },
  { value: 'training', label: 'Formation', color: 'bg-blue-100 text-blue-700' },
  { value: 'personal', label: 'Personnel', color: 'bg-purple-100 text-purple-700' },
  { value: 'other', label: 'Autre', color: 'bg-gray-100 text-gray-700' },
];

export function LeaveRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [comment, setComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchRequests = async () => {
    try {
      const res = await companyApi.getLeaveRequests();
      setRequests(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const getLeaveTypeConfig = (type) => LEAVE_TYPES.find(t => t.value === type) || LEAVE_TYPES[4];

  const calculateDays = (start, end) => differenceInDays(parseISO(end), parseISO(start)) + 1;

  const formatDateRange = (start, end) => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    if (start === end) {
      return format(startDate, 'd MMMM yyyy', { locale: fr });
    }
    return `${format(startDate, 'd MMM', { locale: fr })} → ${format(endDate, 'd MMM yyyy', { locale: fr })}`;
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setComment('');
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;
    
    setIsProcessing(true);
    try {
      if (actionType === 'approve') {
        await companyApi.approveLeaveRequest(selectedRequest.id, comment || null);
        toast.success('Demande approuvée');
      } else {
        await companyApi.rejectLeaveRequest(selectedRequest.id, comment || null);
        toast.success('Demande refusée');
      }
      setSelectedRequest(null);
      setActionType(null);
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors du traitement');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Demandes de Congés & Absences</h1>
          <p className="text-muted-foreground mt-1">Validez ou refusez les demandes de vos collaborateurs</p>
        </div>
        {requests.length > 0 && (
          <Badge variant="outline" className="text-amber-600 border-amber-300 px-3 py-1">
            <Clock className="w-4 h-4 mr-1" />
            {requests.length} en attente
          </Badge>
        )}
      </div>

      {/* Requests list */}
      {requests.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Aucune demande en attente</h3>
            <p className="text-muted-foreground">
              Toutes les demandes de congés et absences ont été traitées
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map(request => {
            const typeConfig = getLeaveTypeConfig(request.leave_type);
            const days = calculateDays(request.start_date, request.end_date);
            
            return (
              <Card key={request.id} className="border-l-4 border-l-amber-400">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Request info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold">
                          {request.collaborator_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{request.collaborator_name}</h3>
                          <p className="text-sm text-muted-foreground">
                            Demande reçue le {format(parseISO(request.created_at), 'd MMM yyyy à HH:mm', { locale: fr })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge className={`${typeConfig.color}`}>
                            {typeConfig.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{formatDateRange(request.start_date, request.end_date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{days} jour{days > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      
                      {request.reason && (
                        <div className="mt-3 flex items-start gap-2 text-sm">
                          <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <span className="text-muted-foreground">"{request.reason}"</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Right: Actions */}
                    <div className="flex flex-col gap-2">
                      <Button 
                        className="rounded-full bg-green-600 hover:bg-green-700"
                        onClick={() => openActionDialog(request, 'approve')}
                        data-testid={`approve-leave-${request.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Approuver
                      </Button>
                      <Button 
                        variant="outline" 
                        className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => openActionDialog(request, 'reject')}
                        data-testid={`reject-leave-${request.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Refuser
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={!!selectedRequest && !!actionType} onOpenChange={() => { setSelectedRequest(null); setActionType(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {actionType === 'approve' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {actionType === 'approve' ? 'Approuver la demande' : 'Refuser la demande'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>
                  Demande de <strong>{selectedRequest.collaborator_name}</strong> pour{' '}
                  <strong>{calculateDays(selectedRequest.start_date, selectedRequest.end_date)} jour(s)</strong>{' '}
                  ({formatDateRange(selectedRequest.start_date, selectedRequest.end_date)})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Commentaire {actionType === 'reject' ? '(recommandé)' : '(optionnel)'}
            </label>
            <Textarea
              placeholder={actionType === 'approve' 
                ? "Ex: Bon congé ! Pensez à prévenir l'équipe..."
                : "Ex: Période chargée, merci de reporter à..."
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
            {actionType === 'reject' && !comment && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Un commentaire aide le collaborateur à comprendre la décision
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedRequest(null); setActionType(null); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleAction}
              disabled={isProcessing}
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : actionType === 'approve' ? (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {actionType === 'approve' ? 'Approuver' : 'Refuser'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
